<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12">

                <!-- Header -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3">
                    <div class="d-flex align-center">
                        <v-icon color="orange darken-1" class="mr-2">mdi-key-outline</v-icon>
                        <div class="text-subtitle-2 font-weight-bold orange--text text--darken-1 flex-grow-1">
                            Recupero Credenziali
                        </div>
                    </div>
                </v-card>

                <!-- Ricerca -->
                <v-card class="rounded-xl elevation-1 px-3 py-2 mb-3">
                    <v-text-field
                        v-model="query"
                        label="Cerca per nome o cognome"
                        prepend-inner-icon="mdi-magnify"
                        clearable
                        hide-details
                        dense
                        outlined
                        rounded
                        @input="onInput"
                        @click:clear="onClear"
                    />
                </v-card>

                <!-- Loading -->
                <div v-if="loading" class="d-flex justify-center py-6">
                    <v-progress-circular indeterminate color="orange darken-1" size="28" width="3" />
                </div>

                <!-- Nessun risultato -->
                <div v-else-if="searched && risultati.length === 0" class="text-body-2 grey--text text-center py-6">
                    Nessun oratore trovato
                </div>

                <!-- Risultati -->
                <template v-else>
                    <v-card
                        v-for="(o, i) in risultati"
                        :key="i"
                        class="rounded-xl elevation-1 mb-2 px-3 py-2"
                    >
                        <div class="d-flex align-start">
                            <v-icon color="orange darken-1" class="mr-2 mt-1" small>mdi-account-outline</v-icon>
                            <div class="flex-grow-1">
                                <div class="text-subtitle-2 font-weight-bold">{{ o.nominativo }}</div>
                                <div class="text-caption grey--text text--darken-1">
                                    <v-icon x-small class="mr-1">mdi-home-city-outline</v-icon>{{ o.congregazione || '—' }}
                                    &nbsp;·&nbsp;
                                    <v-icon x-small class="mr-1">mdi-domain</v-icon>{{ o.circoscrizione || '—' }}
                                </div>
                                <div class="mt-1 d-flex flex-wrap align-center" style="gap: 6px">
                                    <v-chip x-small label outlined color="grey darken-1">
                                        <v-icon x-small left>mdi-identifier</v-icon>{{ o.codice }}
                                    </v-chip>
                                    <v-chip x-small label outlined color="orange darken-1">
                                        <v-icon x-small left>mdi-lock-outline</v-icon>{{ o.password ? '••••••' : '—' }}
                                    </v-chip>
                                    <v-chip v-if="o.mail && editIndex !== i" x-small label outlined color="primary">
                                        <v-icon x-small left>mdi-email-outline</v-icon>{{ o.mail }}
                                    </v-chip>
                                    <v-chip v-if="o.isAdmin"        x-small label dark color="orange darken-1">APP-ADM</v-chip>
                                    <v-chip v-if="o.isCoordinatore" x-small label dark color="deep-orange darken-4">ADM</v-chip>
                                    <v-chip v-if="o.isAssistente"   x-small label dark color="grey darken-2">ASSISTENTE</v-chip>
                                    <v-chip v-if="o.isAnziano"      x-small label dark color="teal">A</v-chip>
                                    <v-chip v-if="o.isSM"           x-small label dark color="blue darken-1">SM</v-chip>
                                    <v-btn icon x-small @click="apriEmail(o, i)" :color="copiato === i ? 'success' : 'grey darken-1'" class="ml-1">
                                        <v-icon x-small>{{ copiato === i ? 'mdi-check' : 'mdi-message-text-outline' }}</v-icon>
                                    </v-btn>
                                    <v-btn icon x-small :loading="resettingIndex === i" color="orange darken-1" class="ml-1" @click="resetPassword(o, i)">
                                        <v-icon x-small>mdi-lock-reset</v-icon>
                                    </v-btn>
                                </div>

                                <!-- Editor inline email -->
                                <div v-if="editIndex === i" class="mt-2 d-flex align-center" style="gap: 8px">
                                    <v-text-field
                                        v-model="editMail"
                                        label="Email"
                                        prepend-inner-icon="mdi-email-outline"
                                        dense outlined rounded hide-details
                                        autocapitalize="none"
                                        style="max-width: 300px"
                                        @keyup.enter="salvaCredenziali(o, i)"
                                    />
                                    <v-btn small rounded color="primary" :loading="savingIndex === i" @click="salvaCredenziali(o, i)">
                                        <v-icon small left>mdi-content-save-outline</v-icon>Salva
                                    </v-btn>
                                    <v-btn icon small @click="chiudiEmail">
                                        <v-icon small color="grey">mdi-close</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </v-card>

                    <div v-if="risultati.length === 50" class="text-caption grey--text text-center mt-2">
                        Mostrati i primi 50 risultati — affina la ricerca
                    </div>
                </template>

            </v-col>
        </v-row>

        <v-snackbar v-model="snack" :color="snackColor" rounded="pill" timeout="2500" bottom>
            <v-icon dark small class="mr-2">{{ snackIcon }}</v-icon>{{ snackMsg }}
        </v-snackbar>
    </v-container>
</template>

<script>
import { callService, callPost, STD_PASSWORD, copyToClipboard } from '@/modules/utilities.js'

export default {
    name: 'RecuperoCredenziali',

    data() {
        return {
            query:       '',
            risultati:   [],
            loading:     false,
            searched:    false,
            computing:   false,
            debounce:    null,
            copiato:     null,
            editIndex:     null,
            editMail:      '',
            savingIndex:   null,
            resettingIndex: null,
            snack:       false,
            snackColor:  'success',
            snackIcon:   'mdi-check',
            snackMsg:    ''
        }
    },

    methods: {
        onInput() {
            clearTimeout(this.debounce)
            if (!this.query || this.query.trim().length < 2) {
                this.risultati = []
                this.searched  = false
                this.computing = false
                return
            }
            this.debounce = setTimeout(() => this.cerca(), 400)
        },

        onClear() {
            this.query     = ''
            this.risultati = []
            this.searched  = false
            this.computing = false
            this.editIndex = null
        },

        async cerca() {
            this.loading   = true
            this.editIndex = null
            const q = encodeURIComponent(this.query.trim())
            const res = await callService(`/api/dashboard/cercaOratore?q=${q}`, [])
            this.loading  = false
            this.searched = true
            if (res && Array.isArray(res)) {
                this.risultati = res
            }
        },

        apriEmail(o, i) {
            this.copiaMsgCredenziali(o, i)
            this.editIndex = i
            this.editMail  = o.mail || ''
        },

        chiudiEmail() {
            this.editIndex = null
            this.editMail  = ''
        },

        copiaMsgCredenziali(o, i) {
            const nome     = (o.nome || '').trim()
            const username = o.mail || (o.codice + '-' + o.partition)
            const label    = o.mail ? 'email' : 'username'
            const mailFrase = o.mail ? '\n\nHo impostato la tua mail nell\'anagrafica per eventuale recupero account in futuro.' : ''
            const testo    = `Ciao ${nome}, ecco le tue credenziali:\n\n${label}: ${username}\npassword: ${STD_PASSWORD}${mailFrase}\n\nCiao\nLuigi`
            copyToClipboard(testo)
            this.copiato = i
            setTimeout(() => { this.copiato = null }, 2000)
        },

        async salvaCredenziali(o, i) {
            this.savingIndex = i
            const res = await callPost('/api/dashboard/salvaMail', {
                partition:       this.$store.state.partition,
                username:        String(this.$store.state.oratore.codice),
                token:           this.$store.state.oratore.token,
                targetPartition: o.partition,
                codice:          o.codice,
                mail:            this.editMail.trim(),
                password:        null,
            })
            this.savingIndex = null
            if (res === true) {
                o.mail = this.editMail.trim()
                this.chiudiEmail()
                this.snackColor = 'success'
                this.snackIcon  = 'mdi-check'
                this.snackMsg   = 'Email salvata'
            } else if (res?.errore === 'email_duplicata') {
                this.snackColor = 'error'
                this.snackIcon  = 'mdi-alert-circle'
                this.snackMsg   = 'Email già associata ad un altro oratore'
            } else {
                this.snackColor = 'error'
                this.snackIcon  = 'mdi-alert-circle'
                this.snackMsg   = 'Errore nel salvataggio'
            }
            this.snack = true
        },

        async resetPassword(o, i) {
            this.resettingIndex = i
            const res = await callPost('/api/dashboard/salvaMail', {
                partition:       this.$store.state.partition,
                username:        String(this.$store.state.oratore.codice),
                token:           this.$store.state.oratore.token,
                targetPartition: o.partition,
                codice:          o.codice,
                mail:            o.mail,
                password:        STD_PASSWORD,
            })
            this.resettingIndex = null
            if (res === true) {
                o.password = '***reset***'   // segnaposto: la password è stata reimpostata
                this.snackColor = 'success'
                this.snackIcon  = 'mdi-check'
                this.snackMsg   = 'Password reimpostata al valore di default'
            } else {
                this.snackColor = 'error'
                this.snackIcon  = 'mdi-alert-circle'
                this.snackMsg   = 'Errore nel reset'
            }
            this.snack = true
        }
    }
}
</script>
